import * as Sentry from '@sentry/react'
import type { LoggedUser } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import {
  useGetAppInfoUnreadMessages,
  useSetReadAppInfoMessage,
} from 'BrokerTsLib/src/hooks/useAppInfoMessage'
import { createContext, useContext, useEffect } from 'react'

import { useApp } from '@/hooks/useApp'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { useIsLogged, useLogout } from 'BrokerTsLib/src/hooks/useAccount'

type AuthContextProps = {
  user: LoggedUser | null
  carkulkaUnreadMessages: number
  chaloupkaUnreadMessages: number
  cestovkoUnreadMessages: number
  isLogged: boolean
  isLoading: boolean
  setReadMessage: (msgId: number) => void
  reloadUnreadMessages: Function
}

export const AuthContext = createContext<AuthContextProps | null>(null)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { logout, isLoggingOut } = useLogout()
  const { data: user, isLoading, error } = useIsLogged()
  const { environment } = useApp()

  useEffect(() => {
    if (error && !isIn(error?.code, 'ERR_NETWORK', 'ECONNABORTED', 'ERR_CANCELED') && !isLoggingOut && environment !== 'development')
      logout()
  }, [error, logout, isLoggingOut, environment])

  const isLogged = !!user?.logged

  const { refetch: refetchCarkulkaMessages, data: carkulkaMessages } =
    useGetAppInfoUnreadMessages(Product.VehicleInsurance, {
      enabled: !isLoading && isLogged,
    })
  const carkulkaUnreadMessages = carkulkaMessages?.data ?? 0
  const { refetch: refetchChaloupkaMessages, data: chaloupkaMessages } =
    useGetAppInfoUnreadMessages(Product.PropertyInsurance, {
      enabled: !isLoading && isLogged,
    })
  const chaloupkaUnreadMessages = chaloupkaMessages?.data ?? 0
  const { refetch: refetchCestovkoMessages, data: cestovkoMessages } =
    useGetAppInfoUnreadMessages(Product.TravelInsurance, {
      enabled: !isLoading && isLogged
    })
  const cestovkoUnreadMessages = cestovkoMessages?.data ?? 0

  const { mutateAsync: setMessageRead } = useSetReadAppInfoMessage()

  const reloadUnreadMessages = () => {
    refetchCarkulkaMessages()
    refetchChaloupkaMessages()
    refetchCestovkoMessages()
  }

  const setReadMessage = (msgId: number) => {
    setMessageRead({ id: msgId, read: true }).then(() => reloadUnreadMessages())
  }

  useEffect(() => {
    if (user)
      Sentry.setUser({
        id: user?.id,
        username: user?.userName
      })
    else
      Sentry.setUser(null)
  }, [user])

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        carkulkaUnreadMessages,
        chaloupkaUnreadMessages,
        cestovkoUnreadMessages,
        isLogged,
        isLoading,
        setReadMessage,
        reloadUnreadMessages,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')

  return ctx
}
